/**
 * Iter 251 — Widgets supplémentaires + Cookbook objections.
 *  - MetaInboxPreviewWidget : 3 derniers threads Meta non lus
 *  - AgendaMiniWidget       : 7 jours d'agenda groupés par jour
 *  - ObjectionCookbookTopWidget : top 5 objections du tenant (auto-apprises)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WidgetShell } from "./widgets";
import {
  MessagesSquare, CalendarDays, BookOpenCheck, Loader2, ArrowRight, Star, MessageCircle, Phone,
} from "lucide-react";

export function MetaInboxPreviewWidget({ api }) {
  const [data, setData] = useState({ items: [], total_unread: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/meta-inbox-preview")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);

  const channelColor = {
    whatsapp: "text-emerald-400 bg-emerald-500/10",
    messenger: "text-blue-400 bg-blue-500/10",
    instagram: "text-pink-400 bg-pink-500/10",
  };

  return (
    <WidgetShell
      title="Messagerie Meta"
      icon={MessagesSquare}
      action={data.total_unread > 0 ? (
        <Link to="/admin/messagerie" className="text-[10px] text-rose-400 hover:underline flex items-center gap-1">
          {data.total_unread} non lu{data.total_unread > 1 ? "s" : ""} →
        </Link>
      ) : (
        <Link to="/admin/messagerie" className="text-[10px] text-cyan-400 hover:underline">Ouvrir →</Link>
      )}
      testid="widget-meta-inbox-preview"
    >
      {loading ? <Spinner /> :
        data.items.length === 0 ? (
          <div className="text-center py-6">
            <MessageCircle className="w-7 h-7 text-slate-700 mx-auto mb-2" />
            <div className="text-xs text-slate-400">Aucun message non lu</div>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {data.items.map((t) => (
              <li key={t.thread_id}>
                <Link to={`/admin/messagerie?thread=${t.thread_id}`} className="block p-2 rounded hover:bg-white/[0.04] border border-transparent hover:border-white/10">
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 text-[9px] uppercase px-1.5 py-0.5 rounded ${channelColor[t.channel] || "text-slate-400 bg-slate-500/10"}`}>
                      {t.channel}
                    </span>
                    <span className="text-xs text-white font-medium truncate flex-1">{t.sender_name || t.sender_id}</span>
                    {t.unread_count > 0 && (
                      <span className="shrink-0 text-[9px] bg-rose-500 text-white px-1 rounded-full">{t.unread_count}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate mt-0.5">{t.last_message_preview || "—"}</div>
                </Link>
              </li>
            ))}
          </ul>
        )
      }
    </WidgetShell>
  );
}

export function AgendaMiniWidget({ api }) {
  const [data, setData] = useState({ by_day: {}, count: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/agenda-mini")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);

  const days = Object.keys(data.by_day || {}).sort();
  return (
    <WidgetShell title="Agenda 7 jours" icon={CalendarDays} testid="widget-agenda-mini">
      {loading ? <Spinner /> :
        days.length === 0 ? (
          <div className="text-center py-6">
            <CalendarDays className="w-7 h-7 text-slate-700 mx-auto mb-2" />
            <div className="text-xs text-slate-400">Aucun événement cette semaine</div>
          </div>
        ) : (
          <div className="space-y-2">
            {days.map((d) => (
              <div key={d}>
                <div className="text-[10px] uppercase text-emerald-400 font-bold mb-1">
                  {formatDay(d)}
                </div>
                <ul className="space-y-1">
                  {data.by_day[d].map((e) => (
                    <li key={e.event_id} className="text-[11px] text-white bg-white/[0.02] border border-white/5 rounded p-1.5 flex items-center justify-between gap-2">
                      <span className="truncate">{e.title || "—"}</span>
                      <span className="text-[10px] text-slate-400 shrink-0">{formatTime(e.start_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )
      }
    </WidgetShell>
  );
}

export function ObjectionCookbookTopWidget({ api }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/objection-cookbook-top")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);

  return (
    <WidgetShell
      title="Cookbook objections"
      icon={BookOpenCheck}
      action={
        <Link to="/admin/objection-cookbook" className="text-[10px] text-cyan-400 hover:underline flex items-center gap-1">
          Voir tout ({data.total || 0}) <ArrowRight className="w-3 h-3" />
        </Link>
      }
      testid="widget-objection-cookbook-top"
    >
      {loading ? <Spinner /> :
        data.items.length === 0 ? (
          <div className="text-center py-4 text-xs text-slate-400">
            Aucune objection enregistrée
            <div className="text-[10px] text-slate-600 mt-1">
              Le cookbook se remplit automatiquement quand vous utilisez les suggestions du Coach IA pendant un appel.
            </div>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {data.items.map((obj) => (
              <li key={obj.objection_id} className="bg-white/[0.02] border border-white/5 rounded p-2">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {obj.favorite && <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />}
                    <span className="text-xs text-white font-medium truncate">{obj.label}</span>
                  </div>
                  <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300">
                    ×{obj.used_count}
                  </span>
                </div>
                {obj.agent_native && (
                  <div className="text-[11px] text-slate-300 italic line-clamp-2">
                    {obj.agent_native}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )
      }
    </WidgetShell>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-cyan-400" /></div>;
}

function formatDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T12:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today.getTime() + 86400000);
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    if (target.getTime() === today.getTime()) return "Aujourd'hui";
    if (target.getTime() === tomorrow.getTime()) return "Demain";
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "short" });
  } catch { return iso; }
}

function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export const _unused = { Phone };
